import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Dialog, TextField } from "@mui/material";
import { useFormik } from "formik";
import { MyTimesheetShowRequestDialogHeader } from "my-timesheet/components";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import * as Yup from "yup";

type MyTimesheetNewProjectDialogProps = {
  openAddProjectDialog: boolean;
  closeAddProjectDialog: (value?: boolean) => void;
  createMySheetProject: (args: {
    variables: { name: string; description: string };
  }) => Promise<any>;
};

export const MyTimesheetNewProjectDialog = ({
  openAddProjectDialog,
  closeAddProjectDialog,
  createMySheetProject,
}: MyTimesheetNewProjectDialogProps) => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();

  const formik = useFormik({
    initialValues: {
      newProjectName: "",
      projectDescription: "",
    },
    validationSchema: Yup.object().shape({
      newProjectName: Yup.string().required(t("OC_NAME_REQUIRED")),
      projectDescription: Yup.string().max(
        255,
        t("MY_TIMESHEET_DESCRIPTION_TOO_LONG"),
      ),
    }),
    onSubmit: async (values) => {
      const projectData = {
        name: values.newProjectName,
        description: values.projectDescription,
      };
      try {
        await createMySheetProject({ variables: projectData });
        closeAddProjectDialog(false);
        formik.resetForm();
      } catch (error) {
        console.error("Failed to create project", error);
      }
    },
  });

  return (
    <Dialog
      open={openAddProjectDialog}
      onClose={(_event, _reason) => closeAddProjectDialog(false)}
      fullScreen={isMobile ? true : false}
    >
      <Box padding="0px 16px 16px 16px">
        <MyTimesheetShowRequestDialogHeader
          label={t("MY_TIMESHEET_NEW_PROJECT")}
          close={closeAddProjectDialog}
          isMobile={isMobile}
        />
        <Box
          style={{
            height: "2px",
            backgroundColor: "#C6CACC",
            marginBottom: "16px",
          }}
        />
        <Box height="16px" />
        <TextField
          id="new-project-name"
          name="newProjectName"
          label={t("MY_TIMESHEET_PROJECT_NAME")}
          variant="outlined"
          style={{ width: "100%" }}
          value={formik.values.newProjectName}
          onChange={formik.handleChange}
          error={
            formik.touched.newProjectName &&
            Boolean(formik.errors.newProjectName)
          }
          helperText={
            formik.touched.newProjectName && formik.errors.newProjectName
          }
        />
        <Box height="32px" />
        <TextField
          id="project-description"
          label={t("OC_NOTE")}
          name="projectDescription"
          multiline
          minRows={2.3}
          variant="outlined"
          style={{ width: "100%", height: "auto" }}
          value={formik.values.projectDescription}
          onChange={formik.handleChange}
          error={
            formik.touched.projectDescription &&
            Boolean(formik.errors.projectDescription)
          }
          helperText={
            !formik.values.projectDescription
              ? t("OC_OPTIONAL")
              : `${formik.values.projectDescription?.length}/255`
          }
          FormHelperTextProps={
            !formik.values.projectDescription
              ? { style: { textAlign: "left" } }
              : { style: { textAlign: "right" } }
          }
        />
        <Box height="16px" />
        <Box
          style={{
            height: "2px",
            backgroundColor: "#C6CACC",
            marginBottom: "16px",
          }}
        />
        <Box
          display="flex"
          flexDirection={isMobile ? "column-reverse" : "row"}
          justifyContent="space-between"
          width={isMobile ? "100%" : "568px"}
        >
          <Button
            id="cancel-project"
            onClick={() => closeAddProjectDialog(false)}
            color="secondary"
            variant="text"
            style={{
              width: isMobile ? "100%" : "calc(50% - 8px)",
            }}
          >
            {t("OC_CANCEL")}
          </Button>
          {isMobile && <Box height="16px" />}
          <Button
            id="create-project"
            onClick={() => formik.handleSubmit()}
            variant="contained"
            color="primary"
            disableElevation
            style={{
              width: isMobile ? "100%" : "calc(50% - 8px)",
            }}
          >
            {t("MY_TIMESHEET_CREATE")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MyTimesheetNewProjectDialog;
